import type { ReactNode } from 'react';

/**
 * OWNER-OPERATING-SYSTEM-V1 section band (the polish layer s.25.3 section
 * rhythm + s.25.5 collapsible pattern). A page renders as DIFFERENTIATED
 * BANDS: one raised surface per band (elevation s.25.1), members flat
 * inside. Two modes:
 *
 * - STATIC BAND (collapsible=false, the default): a raised section with a
 *   header row (title + basis + at most ONE header action, s.25.10). The
 *   attention band is ALWAYS this mode -- it never collapses.
 * - DISCLOSURE SECTION (collapsible=true): native <details>/<summary> --
 *   summary focusable, Enter/Space toggles, no JS required. Closed sections
 *   show the title only (DENSITY rule 1: the section's numbers render once,
 *   inside). Content reveals instantly; only the chevron animates (s.25.9,
 *   reduced-motion safe in CSS).
 *
 * PRESENTATION-ONLY; composes SectionShell's visual family without touching
 * it (SectionShell keeps its adopters; this is the band-rhythm variant the
 * polish layer adds).
 */

export interface SectionBandProps {
  readonly title: string;
  /** Comparison basis / scope line, printed once beside the title. */
  readonly basis?: string;
  /** At most ONE header affordance (drill link or control). */
  readonly action?: ReactNode;
  readonly collapsible?: boolean;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
  readonly testId?: string;
  /* -- S4 counted-header grammar (PLATFORM-DESIGN-SYSTEM-CODIFICATION-V1;
     owner-home/CONFORMANCE-CONTRACT.md C-8) -- ADDITIVE: existing
     adopters render unchanged when these are omitted. The signature-grade
     wrapper that REQUIRES them is ui/SectionContainer.tsx. */
  /** Header count = the body's enumerable evidence items (C-8 COUNT
   *  RULE; supplementary strips ride `stat`, never the count). */
  readonly count?: number;
  /** Census-traced headline stat printed on the collapsed header. */
  readonly stat?: ReactNode;
  /** Scope printed at header level (a collapsed header is never less
   *  honest than its body). */
  readonly scope?: string;
  /** Body-governing tag (DESIGN-ONLY / cohort window / conversion)
   *  printed at header level too (C-8). */
  readonly tag?: ReactNode;
}

/** The S4 header cluster, shared by both modes (title + count +
 *  census-traced stat + scope + body-governing tag + basis). */
function sectionHeaderContent(props: SectionBandProps): ReactNode {
  const { title, count, stat, scope, tag, basis } = props;
  return (
    <>
      <span className="ac-secband__title">{title}</span>
      {count !== undefined ? (
        <span className="ac-secband__count" data-testid="secband-count">
          {count}
        </span>
      ) : null}
      {stat ? <span className="ac-secband__stat">{stat}</span> : null}
      {scope ? <span className="ac-secband__scope">{scope}</span> : null}
      {tag ? <span className="ac-secband__tag">{tag}</span> : null}
      {basis ? <span className="ac-secband__basis">{basis}</span> : null}
    </>
  );
}

export default function SectionBand(props: SectionBandProps): JSX.Element {
  const {
    action,
    collapsible = false,
    defaultOpen = false,
    children,
    testId,
  } = props;
  if (collapsible) {
    return (
      <details className="ac-secband" open={defaultOpen} data-testid={testId}>
        <summary className="ac-secband__summary">
          {sectionHeaderContent(props)}
        </summary>
        <div className="ac-secband__body">{children}</div>
      </details>
    );
  }
  return (
    <section className="ac-secband ac-secband--static" data-testid={testId}>
      <div className="ac-secband__head">
        {sectionHeaderContent(props)}
        {action ? <span className="ac-secband__action">{action}</span> : null}
      </div>
      <div className="ac-secband__body">{children}</div>
    </section>
  );
}
